import { faEdit, faEllipsisH, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import Icon from '@/components/general/Icon';
import { QuestionFrequencyDataType } from '@/types/api';
import styles from './RotatingQuestionItem.styles';

type RotatingQuestionItemComponentProps = {
  question: string;
  buttonMenuElement: HTMLElement | null;
  isDeleteDialogOpen: boolean;
  handleToggleButtonMenu: (event: any | null) => void;
  handleEditQuestion: () => void;
  handleClickOnDelete: () => void;
  handleOpenDialog: () => void;
  handleCloseDialog: () => void;
  frequency?: QuestionFrequencyDataType;
  nextCheckinStartDate?: string | null;
  nextCheckinEndDate?: string | null;
};

const RotatingQuestionItemComponent = function ({
  question,
  buttonMenuElement,
  handleToggleButtonMenu,
  handleEditQuestion,
  handleClickOnDelete,
  isDeleteDialogOpen,
  handleOpenDialog,
  handleCloseDialog,
  frequency,
  nextCheckinStartDate,
  nextCheckinEndDate,
}: RotatingQuestionItemComponentProps): JSX.Element {
  return (
    <>
      <Paper variant="outlined" sx={styles.container}>
        <Box display="flex" alignItems="center" paddingX={2} paddingY={1}>
          <Box display="flex" flex={0.95} alignItems="center">
            <Box display={{ xs: 'flex', sm: 'block' }} flexDirection={{ xs: 'column', sm: 'row' }}>
              {frequency && (
                <Typography
                  component="span"
                  sx={styles.boxFrequency}
                  color="primary.dark"
                  fontWeight={600}
                  fontSize={13}
                >
                  {frequency.title}
                </Typography>
              )}

              <Typography
                component="span"
                color="primary.dark"
                fontWeight={600}
                lineHeight={1.75}
                data-testid="rotating-question-label"
              >
                {question}
              </Typography>

              {nextCheckinStartDate && nextCheckinEndDate && (
                <Typography component="span" color="primary.light" sx={styles.date}>
                  {nextCheckinStartDate} - {nextCheckinEndDate}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" flex={0.05} alignItems="center" justifyContent="center">
            <IconButton
              onClick={handleToggleButtonMenu}
              data-testid="rotating-question-show-menu-button"
              aria-label="Show rotating question menu"
            >
              <Icon icon={faEllipsisH} size={20} />
            </IconButton>

            <Menu
              id="rotating-question-button-menu"
              keepMounted
              anchorEl={buttonMenuElement}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(buttonMenuElement)}
              onClose={handleToggleButtonMenu}
              elevation={0}
              sx={styles.buttonMenu}
            >
              <MenuItem onClick={handleEditQuestion} data-testid="rotating-question-edit-button">
                <Icon icon={faEdit} color="primary" sx={{ marginRight: 1 }} />
                <Typography color="primary">Edit</Typography>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={handleOpenDialog}
                sx={{ opacity: 0.75 }}
                data-testid="rotating-question-open-dialog"
              >
                <Icon icon={faTrash} color="error" variantColor="light" sx={{ marginRight: 1 }} />
                <Typography color="error.light">Delete</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDialog}
        data-testid="delete-rotating-question-dialog"
      >
        <DialogTitle>Delete question?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure about deleting this question?{' '}
            <Typography component="span" fontWeight={600}>
              This action cannot be reverted.
            </Typography>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClickOnDelete}
            data-testid="rotating-question-delete-button"
          >
            Delete
          </Button>
          <Button color="auxRed" onClick={handleCloseDialog} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RotatingQuestionItemComponent;
